import { differenceInCalendarDays } from "date-fns";

export interface AlignmentItem {
  item_type: string;
  date?: string | null;
  check_in?: string | null;
}

export interface ProgrammeAlignment {
  firstItemDate: string | null;
  lastItemDate: string | null;
  leadingEmptyDays: number;
  trailingEmptyDays: number;
  needsRealign: boolean;
}

/** Parse "yyyy-MM-dd" (or ISO) as a UTC date, matching dayGroupingUtils. */
export function parseAsUTC(dateStr: string): Date {
  const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function relevantDate(item: AlignmentItem): string | null {
  const raw = item.item_type === "accommodation" ? item.check_in : item.date;
  return raw ? raw.split("T")[0] : null;
}

/**
 * Compare the programme's stored travel dates against the dates that items
 * actually occupy, so empty leading/trailing days can be trimmed.
 */
export function analyseProgrammeAlignment(
  items: AlignmentItem[],
  startDate?: string | null,
  endDate?: string | null
): ProgrammeAlignment {
  const dates = items
    .map(relevantDate)
    .filter((d): d is string => !!d)
    .sort();

  const firstItemDate = dates[0] ?? null;
  const lastItemDate = dates[dates.length - 1] ?? null;

  let leadingEmptyDays = 0;
  let trailingEmptyDays = 0;

  if (startDate && firstItemDate) {
    leadingEmptyDays = Math.max(
      0,
      differenceInCalendarDays(parseAsUTC(firstItemDate), parseAsUTC(startDate))
    );
  }
  if (endDate && lastItemDate) {
    trailingEmptyDays = Math.max(
      0,
      differenceInCalendarDays(parseAsUTC(endDate), parseAsUTC(lastItemDate))
    );
  }

  return {
    firstItemDate,
    lastItemDate,
    leadingEmptyDays,
    trailingEmptyDays,
    needsRealign: leadingEmptyDays > 0 || trailingEmptyDays > 0,
  };
}

export function formatProgrammeDate(dateStr: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(parseAsUTC(dateStr));
}
